import { Navigate, type RouteObject } from 'react-router'
import { RouteError } from '~/components/RouteError'
import { OrganizerShell } from '~/layouts/OrganizerShell'
import { RequireOrganizer } from '~/layouts/RequireOrganizer'
import { installOrganizerMockApi } from '~/mocks/organizer'
import { page } from './lazy'

// Dev-only in-browser mock of the organizer API (off by default): `VITE_ORGANIZER_MOCK=true pnpm dev`. Vite replaces
// the flag at build time, so without it the call (and the mock module) is dropped from the bundle.
if (import.meta.env.VITE_ORGANIZER_MOCK === 'true') installOrganizerMockApi()

/**
 * Organizer route module. OWNER: B1 (add / reorganize organizer routes here only).
 *
 *   /organizer/login | /signup | /logout                 organizer auth (public layout)
 *   /organizer                                           my organizations (cards)
 *   /organizer/orgs/new[?org=:orgId&step=]               new-organization wizard (continues a draft with ?org=)
 *   /organizer/orgs/:orgId                               → overview
 *   /organizer/orgs/:orgId/overview                      KPIs, join link + QR, votings summary
 *   /organizer/orgs/:orgId/members[/:memberId]           members table / verbose member detail
 *   /organizer/orgs/:orgId/review                        review queue
 *   /organizer/orgs/:orgId/map                           members map
 *   /organizer/orgs/:orgId/communications                compose, test send, history
 *   /organizer/orgs/:orgId/votings                       votings list
 *   /organizer/orgs/:orgId/votings/new[?voting=:id&step=] voting wizard (continues a draft with ?voting=)
 *   /organizer/orgs/:orgId/votings/:votingId[/results|/settings]   voting dashboard
 *   /organizer/orgs/:orgId/committees                    election committees (pairing code, link + fingerprint)
 *   /organizer/orgs/:orgId/settings                      registration rules, districts, test mode
 *
 * `:votingId` is the API `electionId`.
 */

/** Rendered inside PublicLayout (no organizer session needed). */
export const organizerPublicRoutes: RouteObject[] = [
  { path: 'organizer/login', lazy: page(() => import('~/pages/organizer/LoginPage')) },
  { path: 'organizer/signup', lazy: page(() => import('~/pages/organizer/SignupPage')) },
  { path: 'organizer/logout', lazy: page(() => import('~/pages/organizer/LogoutPage')) },
]

/** The authenticated organizer dashboard (own shell). */
export const organizerRoutes: RouteObject = {
  path: 'organizer',
  element: (
    <RequireOrganizer>
      <OrganizerShell />
    </RequireOrganizer>
  ),
  errorElement: <RouteError />,
  children: [
    { index: true, lazy: page(() => import('~/pages/organizer/OrganizationsListPage')) },
    { path: 'orgs/new', lazy: page(() => import('~/pages/organizer/NewOrganizationPage')) },
    {
      path: 'orgs/:orgId',
      lazy: page(() => import('~/pages/organizer/org/OrgLayout')),
      children: [
        { index: true, element: <Navigate to='overview' replace /> },
        { path: 'overview', lazy: page(() => import('~/pages/organizer/org/OverviewTab')) },
        { path: 'members', lazy: page(() => import('~/pages/organizer/org/MembersTab')) },
        { path: 'members/:memberId', lazy: page(() => import('~/pages/organizer/org/MemberDetailPage')) },
        { path: 'review', lazy: page(() => import('~/pages/organizer/org/ReviewTab')) },
        { path: 'map', lazy: page(() => import('~/pages/organizer/org/MapTab')) },
        { path: 'communications', lazy: page(() => import('~/pages/organizer/org/CommunicationsTab')) },
        { path: 'votings', lazy: page(() => import('~/pages/organizer/org/VotingsTab')) },
        { path: 'votings/new', lazy: page(() => import('~/pages/organizer/org/NewVotingPage')) },
        {
          path: 'votings/:votingId',
          lazy: page(() => import('~/pages/organizer/org/voting/VotingLayout')),
          children: [
            { index: true, lazy: page(() => import('~/pages/organizer/org/voting/VotingOverviewTab')) },
            { path: 'results', lazy: page(() => import('~/pages/organizer/org/voting/VotingResultsTab')) },
            { path: 'settings', lazy: page(() => import('~/pages/organizer/org/voting/VotingSettingsTab')) },
          ],
        },
        { path: 'committees', lazy: page(() => import('~/pages/organizer/org/CommitteesTab')) },
        { path: 'settings', lazy: page(() => import('~/pages/organizer/org/SettingsTab')) },
      ],
    },
  ],
}
